import { USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY } from '../../utils/constants';

/**
 * PbDQ workspaces and solutions — resolved entirely on the client.
 *
 * WHY NO BACKEND: both inputs the navigation needs are already on `authUser`
 * at sign-in — `access_level` and `product_access`. Asking the server to
 * restate them would be a second round trip for data the browser already
 * holds, and a second place for the two to disagree after a workspace switch.
 *
 * This layer is menu VISIBILITY only. Real enforcement stays server-side in the
 * product guards; nothing here is a security boundary.
 */

/** The five PbDQ workspaces, from the Navigation Bar sheet. */
export const PBDQ_WORKSPACE = {
    PARTNER: 'PW',
    NETWORK: 'SNW',
    SCHOOL: 'SW',
    TEACHER: 'TW',
    INDIVIDUAL: 'IW',
};

/**
 * Solution codes — the "Solution Type" column of the sheet.
 *
 * Separate from the existing DQ_PRO_PLAN_KEY on purpose. That also uses P0-P4
 * but means different things — its P1 is "Learning Tracker", PbDQ's P1 is the
 * "COS Readiness Solution". The two schemes are deliberately NOT mapped onto
 * each other, and nothing existing is renamed.
 */
export const SOLUTION = {
    /** A row tag meaning "always show" — never something a workspace holds. */
    ALL: 'ALL',
    /** COS Children Assessment. */
    P0: 'P0',
    /** COS Readiness Solution. */
    P1: 'P1',
    /** P1 with provider-created programs. */
    P1A: 'P1A',
    /** P1 with LearnCreator distributed to schools. */
    P1B: 'P1B',
    /** LearnCreator. */
    LC: 'LC',
    /** COS Government Checklist. */
    GC: 'GC',
};

/**
 * Which PLAN grants which solutions.
 *
 * The plans collection now carries a key per PbDQ solution, so this is a direct
 * mapping rather than an inference from products:
 *
 *   enterprise_p0    COS Children Assessment                     → P0
 *   enterprise_p1    COS Readiness Solution                      → P1
 *   enterprise_p1a   …with provider-created programs             → P1 + P1A
 *   enterprise_p1b   …with teacher-created programs              → P1 + P1B
 *   enterprise_lc    LearnCreator                                → LC
 *
 * P1A and P1B each grant P1 as well, because the sheet tags some rows plainly
 * "P1" and those must show for both delivery modes — see the School block, where
 * Performance Summary is enumerated P0 / P1A / P1B / LC while My Classes rows
 * are tagged plainly P1.
 *
 * GC (COS Government Checklist) has no plan and never will: the Button Registry
 * describes it as an access-request flow for national governments (BTN-0018), so
 * it is granted out of band. Every GC row in the sheet is tagged ALL, so nothing
 * depends on GC resolving.
 *
 * An earlier version tried to derive solutions from `product_access`. That could
 * only ever resolve P0 — no product corresponds to the COS Readiness Solution —
 * which is why gating stayed switched off.
 */
export const PLAN_TO_SOLUTIONS = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]: [SOLUTION.P0],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]: [SOLUTION.P1, SOLUTION.P1A, SOLUTION.P1B],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1A]: [SOLUTION.P1, SOLUTION.P1A],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: [SOLUTION.P1, SOLUTION.P1B],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]: [SOLUTION.LC],
    // Direct plans sell their enterprise plan's solution (23 Sep 2026) — in ONE variant
    // (30 Sep 2026). Direct P1 is P1B only: P1A rows (request a program from the partner,
    // partner-suggested programs) assume a partner, and a direct buyer has none.
    [DQ_PRO_PLAN_KEY.DIRECT_P0]: [SOLUTION.P0],
    [DQ_PRO_PLAN_KEY.DIRECT_P1B]: [SOLUTION.P1, SOLUTION.P1B],
    [DQ_PRO_PLAN_KEY.DIRECT_LC]: [SOLUTION.LC],
    // Legacy key, until the API's split_direct_variant_plans script has run everywhere.
    [DQ_PRO_PLAN_KEY.DIRECT_P1]: [SOLUTION.P1, SOLUTION.P1B],
};

/**
 * Is solution gating live?
 *
 * TRUE. A row now shows only when the workspace holds one of its solutions.
 *
 * Three things make that safe, each of which caught a real breakage when the
 * whole matrix was simulated plan-by-plan before switching it on:
 *
 * 1. NON-PbDQ PLANS ARE NEVER GATED. resolveSolutions returns null for default,
 *    dq_pro, educator, enterprise and enterprise_p2/p3/p4, and isSolutionVisible
 *    reads null as "do not gate". Returning [] instead would have stripped My
 *    Programs and LearnCreator from every existing DQ Pro customer.
 *
 * 2. TEACHER IS GATED AT THE DOOR, NOT PER ROW. Every TW row carries the same
 *    tag "P1B, LC", because the sheet publishes the whole workspace for those
 *    two solutions. Applied per row that is all-or-nothing, and "nothing" is a
 *    sidebar with no Home on it. TeacherMainList passes solutions={null}; see
 *    the note there.
 *
 * 3. GENERIC P1 HOLDS BOTH DELIVERY MODES. enterprise_p1 predates the P1A/P1B
 *    split, and the P1 school flow is built around it — PrivateRouteWithLayout
 *    carries explicit isP1School exemptions so that account can reach the
 *    programs its partner delivered. Mapping it to P1 alone would hide a menu
 *    entry the guard deliberately allows.
 *
 * What this yields, verified against the sheet row by row:
 *   PW  P0  hides My Programs (+Create, Learning Programs), LearnCreator,
 *           My Program Adoption   ·   LC hides Attention Needed
 *           P1A hides My Program Adoption   ·   P1/P1B hide nothing
 *   SW  P0  hides My Programs (+children) and LearnCreator
 *           P1A hides LearnCreator   ·   P1/P1B/LC hide nothing
 *
 * P0 losing My Programs is intended, not a regression: the sheet tags that item
 * P1/LC, and the P0 Home board reaches the assessment through Step 2 instead.
 *
 * This is menu visibility only. The route guard and the server are unchanged and
 * remain the real enforcement.
 */
export const SOLUTION_GATING_READY = true;

/**
 * Which workspace this user is in, or null.
 *
 * Null is a real answer, not a failure — an admin or analytics account has no
 * PbDQ workspace. Never default to Partner: that would hand a school account the
 * partner menu.
 *
 * No Network entry exists because no access level corresponds to it.
 */
export const resolveWorkspace = (user) => {
    switch (Number(user && user.access_level)) {
        case USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL:
        case USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE:
            return PBDQ_WORKSPACE.PARTNER;
        case USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE:
            return PBDQ_WORKSPACE.SCHOOL;
        case USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL:
            return PBDQ_WORKSPACE.TEACHER;
        case USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL:
            return PBDQ_WORKSPACE.INDIVIDUAL;
        default:
            return null;
    }
};

/**
 * Which solutions this workspace has unlocked, or NULL for "not a PbDQ account".
 *
 * Null is the important return value. It means this user is on a plan the PbDQ
 * sheet was never written about — default, dq_pro, educator, enterprise,
 * enterprise_p2/p3/p4 — and `isSolutionVisible` treats it as "do not gate".
 * Returning an empty array instead would hide every row the sheet tags P1 or LC
 * and gut the menu for existing DQ Pro customers.
 *
 * A school (level 22) and a teacher (level 23) inherit the workspace owner's
 * plan, so they resolve to the same solutions as the partner above them — which
 * is what the sheet's School and Teacher blocks assume.
 *
 * ALL is never returned; it is a row tag the renderer applies.
 */
export const resolveSolutions = (user) => {
    const planKey = user && user.dq_pro_plan && user.dq_pro_plan.plan_key;
    return PLAN_TO_SOLUTIONS[planKey] || null;
};

/**
 * Is a row visible for the solutions this workspace holds?
 *
 * Three ways to be visible:
 *   · `unlocked` is null — not a PbDQ plan, so solution tags do not apply
 *   · the row lists no solutions — the sheet leaves the column blank on
 *     continuation rows, and hiding those would drop real menu entries
 *   · the row is tagged ALL, or shares a solution with this workspace
 *
 * This is menu visibility only. Real enforcement stays in the route guard and on
 * the server — hiding a row has never been a substitute for either.
 */
export const isSolutionVisible = (rowSolutions, unlocked = null) => {
    if (unlocked === null || unlocked === undefined) return true;
    if (!rowSolutions || rowSolutions.length === 0) return true;
    if (rowSolutions.includes(SOLUTION.ALL)) return true;
    return rowSolutions.some((code) => unlocked.includes(code));
};
